import { supabase } from '../lib/supabase'
import type {
  CreateN8nFlowPayload,
  N8nFlow,
  N8nFlowFilters,
  N8nFlowVersion,
  UpdateN8nFlowPayload,
} from '../types/n8nFlows'

function parseFlowJson(raw: string): unknown {
  const trimmed = raw.trim()
  if (!trimmed) {
    throw new Error('JSON é obrigatório')
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    throw new Error('JSON inválido')
  }
}

async function getMultipleUsersInfo(
  userIds: string[]
): Promise<Record<string, { name: string; email: string }>> {
  try {
    const { data, error } = await supabase.rpc('get_multiple_users_info', {
      user_ids: userIds,
    })

    if (error || !data) return {}

    const userMap: Record<string, { name: string; email: string }> = {}
    if (Array.isArray(data)) {
      data.forEach((user: { id: string; name?: string; email?: string }) => {
        userMap[user.id] = {
          name: user.name || 'Usuário',
          email: user.email || '',
        }
      })
    }
    return userMap
  } catch {
    return {}
  }
}

async function createFlowVersion(
  flowId: string,
  versionNumber: number,
  title: string,
  description: string | null,
  flowJson: unknown,
  changeSummary?: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { error } = await supabase.from('n8n_flow_versions').insert({
    flow_id: flowId,
    version_number: versionNumber,
    title,
    description,
    flow_json: flowJson,
    created_by: user.id,
    change_summary: changeSummary,
  })

  if (error) {
    throw new Error(`Erro ao criar versão: ${error.message}`)
  }
}

export async function getFlowVersions(flowId: string): Promise<N8nFlowVersion[]> {
  const { data, error } = await supabase
    .from('n8n_flow_versions')
    .select('*')
    .eq('flow_id', flowId)
    .order('version_number', { ascending: false })

  if (error) {
    throw new Error(`Erro ao buscar versões: ${error.message}`)
  }

  const versions = (data || []) as N8nFlowVersion[]
  const authorIds = Array.from(
    new Set(versions.map(v => v.created_by).filter(Boolean))
  ) as string[]

  let userMap: Record<string, { name: string; email: string }> = {}
  if (authorIds.length > 0) {
    userMap = await getMultipleUsersInfo(authorIds)
  }

  return versions.map(v => ({
    ...v,
    user_name: v.created_by ? userMap[v.created_by]?.name || 'Usuário' : 'Usuário',
  }))
}

export async function restoreFlowVersion(
  flowId: string,
  versionNumber: number
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { data: version, error: versionError } = await supabase
    .from('n8n_flow_versions')
    .select('*')
    .eq('flow_id', flowId)
    .eq('version_number', versionNumber)
    .single()

  if (versionError || !version) {
    throw new Error('Versão não encontrada')
  }

  const { error: updateError } = await supabase
    .from('n8n_flows')
    .update({
      title: version.title,
      description: version.description,
      flow_json: version.flow_json,
      updated_at: new Date().toISOString(),
    })
    .eq('id', flowId)

  if (updateError) {
    throw new Error(`Erro ao restaurar versão: ${updateError.message}`)
  }

  const { data: currentRow } = await supabase
    .from('n8n_flows')
    .select('current_version')
    .eq('id', flowId)
    .single()

  const newVersionNumber = (currentRow?.current_version || 0) + 1

  await createFlowVersion(
    flowId,
    newVersionNumber,
    version.title,
    version.description,
    version.flow_json,
    `Restaurado da versão ${versionNumber}`
  )

  await supabase
    .from('n8n_flows')
    .update({ current_version: newVersionNumber })
    .eq('id', flowId)
}

export async function listFlows(
  filters: N8nFlowFilters = {},
  page: number = 1,
  limit: number = 25
): Promise<{
  flows: N8nFlow[]
  total: number
  page: number
  limit: number
  totalPages: number
}> {
  const sortBy = filters.sort_by || 'updated_at'
  const ascending = filters.sort_order === 'asc'

  let query = supabase
    .from('n8n_flows')
    .select('*')
    .order(sortBy, { ascending })

  let countQuery = supabase
    .from('n8n_flows')
    .select('*', { count: 'exact', head: true })

  if (filters.query?.trim()) {
    const q = filters.query.trim()
    const orFilter = `title.ilike.%${q}%,description.ilike.%${q}%`
    query = query.or(orFilter)
    countQuery = countQuery.or(orFilter)
  }

  const offset = (page - 1) * limit
  const { count: totalCount } = await countQuery
  const { data, error } = await query.range(offset, offset + limit - 1)

  if (error) {
    throw new Error(`Erro ao carregar fluxos: ${error.message}`)
  }

  const flows = (data || []) as N8nFlow[]
  const total = totalCount || 0

  return {
    flows,
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit) || 0,
  }
}

export async function getFlow(id: string): Promise<N8nFlow | null> {
  const { data, error } = await supabase
    .from('n8n_flows')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(`Erro ao carregar fluxo: ${error.message}`)
  }

  return data as N8nFlow
}

export async function createFlow(payload: CreateN8nFlowPayload): Promise<string> {
  const flowJson = parseFlowJson(payload.flow_json)

  const {
    data: { user },
  } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('n8n_flows')
    .insert({
      title: payload.title.trim(),
      description: payload.description.trim() || null,
      flow_json: flowJson,
      created_by: user?.id ?? null,
      current_version: 0,
    })
    .select('id')
    .single()

  if (error || !data) {
    throw new Error(`Erro ao criar fluxo: ${error?.message || 'Erro desconhecido'}`)
  }

  await createFlowVersion(
    data.id,
    1,
    payload.title.trim(),
    payload.description.trim() || null,
    flowJson,
    'Versão inicial'
  )

  await supabase.from('n8n_flows').update({ current_version: 1 }).eq('id', data.id)

  return data.id
}

export async function updateFlow(payload: UpdateN8nFlowPayload): Promise<void> {
  const flowJson = parseFlowJson(payload.flow_json)

  const { error } = await supabase
    .from('n8n_flows')
    .update({
      title: payload.title.trim(),
      description: payload.description.trim() || null,
      flow_json: flowJson,
    })
    .eq('id', payload.id)

  if (error) {
    throw new Error(`Erro ao atualizar fluxo: ${error.message}`)
  }

  const { data: lastVersion } = await supabase
    .from('n8n_flow_versions')
    .select('version_number')
    .eq('flow_id', payload.id)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  const newVersionNumber = (lastVersion?.version_number || 0) + 1

  await createFlowVersion(
    payload.id,
    newVersionNumber,
    payload.title.trim(),
    payload.description.trim() || null,
    flowJson,
    'Atualização do fluxo'
  )

  await supabase
    .from('n8n_flows')
    .update({ current_version: newVersionNumber })
    .eq('id', payload.id)
}

export async function deleteFlow(id: string): Promise<void> {
  const { error: versionsError } = await supabase
    .from('n8n_flow_versions')
    .delete()
    .eq('flow_id', id)

  if (versionsError) {
    throw new Error(`Erro ao excluir versões: ${versionsError.message}`)
  }

  const { error } = await supabase.from('n8n_flows').delete().eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir fluxo: ${error.message}`)
  }
}

export const n8nFlowsService = {
  listFlows,
  getFlow,
  createFlow,
  updateFlow,
  deleteFlow,
  getFlowVersions,
  restoreFlowVersion,
}
